import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Trash2, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { imgUrl } from "@/lib/content";
import { Empty, Loading, PageHead, useAdmin } from "./ui";

const Tile = ({ p, i, n, move, refresh }) => {
  const [cap, setCap] = useState(p.caption);
  const act = async (fn, msg) => { try { await fn(); msg && toast.success(msg); refresh(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="wk-tile" data-testid={`admin-work-${p.id}`}>
      <img src={imgUrl(p.url)} alt="" loading="lazy" />
      <input value={cap} maxLength={120} placeholder="Caption (optional)" onChange={(e) => setCap(e.target.value)}
        onBlur={() => cap !== p.caption && act(() => api.patch(`/admin/work/${p.id}`, { caption: cap }), "Caption saved")} data-testid="admin-work-caption-input" />
      <div className="wk-ctl">
        <button type="button" className="ad-icon-btn" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move left" data-testid="admin-work-left"><ArrowLeft size={15} /></button>
        <button type="button" className="ad-icon-btn" disabled={i === n - 1} onClick={() => move(i, 1)} aria-label="Move right" data-testid="admin-work-right"><ArrowRight size={15} /></button>
        <button type="button" className="ad-icon-btn" onClick={() => window.confirm("Remove this photo from Our Work?") && act(() => api.delete(`/admin/work/${p.id}`), "Photo removed")} aria-label="Remove photo" data-testid="admin-work-delete"><Trash2 size={15} /></button>
      </div>
    </div>
  );
};

export default function WorkAdmin() {
  const { data } = useAdmin("work", "/work");
  const [busy, setBusy] = useState("");
  const qc = useQueryClient();
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin", "work"] }); qc.invalidateQueries({ queryKey: ["work"] }); };
  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    for (const [k, f] of files.entries()) {
      setBusy(`Uploading ${k + 1} of ${files.length}…`);
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data: up } = await api.post("/admin/upload", fd);
        await api.post("/admin/work", { url: up.url });
      } catch (err) { toast.error(`${f.name}: ${formatApiError(err)}`); }
    }
    setBusy("");
    refresh();
    toast.success("Photos added");
  };
  const move = async (i, d) => {
    const ids = data.map((p) => p.id);
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    try { await api.put("/admin/work/order", { ids }); refresh(); } catch (err) { toast.error(formatApiError(err)); }
  };
  if (!data) return <Loading />;
  return (
    <div data-testid="admin-work">
      <PageHead eyebrow="Gallery" title="Our work">
        <label className="btn gold wk-upload" data-testid="admin-work-upload-button">
          {busy ? <Loader2 size={15} className="spin" /> : <Upload size={15} />} {busy || "Add photos"}
          <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden disabled={!!busy} onChange={upload} data-testid="admin-work-upload-input" />
        </label>
      </PageHead>
      <p className="muted-sm">{data.length} photos · shown on the homepage strip and the Our Work page in this order. JPG/PNG/WEBP under 8 MB each.</p>
      {!data.length ? <Empty>No photos yet.</Empty> : (
        <div className="wk-grid">{data.map((p, i) => <Tile key={`${p.id}-${p.caption}`} p={p} i={i} n={data.length} move={move} refresh={refresh} />)}</div>
      )}
    </div>
  );
}
